/**
 * Tenant phone — dynamic fetch + offline fallback.
 *
 * The driver PIN-lockout "Call office" screen and the post-login Waiting
 * screen both need a "tel:" link to the office. The phone lives on the
 * `tenant` row and is the ONE field the driver app exposes pre-auth.
 *
 * Strategy (per Unit #18 brief §3, refined per #18 send-back):
 *  - On mount, fire a non-blocking GET <API_URL>/api/v1/tenant/phone
 *    (public, no auth). The fetch is fire-and-forget — the hook returns
 *    `{status: "loading"}` immediately and the consumer paints the
 *    constant fallback so drivers in a dead-zone still get a working
 *    "Call dispatch" button from the first paint.
 *  - On a successful 2xx (including `{phone: null}`), the hook resolves
 *    to `{status: "ok", phone}` — string OR explicit null. The
 *    distinction matters: the office can clear the phone entirely, and
 *    the driver must see a "not configured" state (no `tel:` link)
 *    instead of a fabricated fallback.
 *  - On network/HTTP/parse failure, the hook resolves to
 *    `{status: "fallback", phone: TENANT_PHONE}` — offline-tolerant.
 *
 * The tri-state is a discriminated union: `status` is the discriminant
 * and `phone`'s nullability is determined by the variant, not encoded
 * separately. `phoneHref(phone: string)` is total over `string` — it
 * never sees a null.
 *
 * The fetch uses `API_URL` (imported from `./api.ts`) as the base — the
 * same one `apiFetch` uses. A relative path would 404 on the Pages SPA
 * fallback in production (every browser hit would request
 * `eagledispatch.work/api/v1/tenant/phone`, never reaching the Worker).
 * The #18 send-back found that bug; the absolute URL fixes it.
 */

import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { API_URL } from "./api";

/** Office contact constant — LAST-DITCH OFFLINE DIAL TARGET ONLY. When the
 * live fetch fails (loading first paint / offline), the driver's "Call
 * Dispatch" button still dials this number — dead zones are a project
 * constraint — but per DISPATCH-PHONE (PM 2026-09-17) it is NO LONGER a
 * displayed number: the button face reads just "Call Dispatch" and the
 * dialed number resolves server-side (active dispatcher →
 * dispatcher_default_phone → tenant.phone). It also remains the shown
 * number in noOpenShiftHelp()'s help line (SPEC-62/66a pins).
 * Canonical home is here, NOT surfaces/driver/constants (SPEC-62 fold #7:
 * reverses a pre-existing components→surfaces dependency; constants
 * re-exports for compat). */
export const TENANT_PHONE = "831-555-0142";

/**
 * Discriminated state of the live phone. Status is the discriminant:
 *  - "loading" : fetch not yet resolved. Paints the constant fallback.
 *  - "ok"      : fetch resolved with a usable value — string OR null.
 *                 `null` means the office cleared the phone; the
 *                 consumer renders a "not configured" state, NOT the
 *                 fallback.
 *  - "fallback" : fetch failed (network/HTTP/parse). The constant is
 *                 the safe default for offline-tolerant driver screens.
 */
export type TenantPhoneState =
  | { status: "loading" }
  | { status: "ok"; phone: string | null }
  | { status: "fallback"; phone: string };

/**
 * Shared "Not clocked in? Call the office" help line — the ONE shape
 * (fold #1). Renders:
 *   - ok + live phone  → tappable tel: link with the live number
 *   - ok + null        → "Office phone not configured" span (office cleared it)
 *   - loading/fallback → tappable tel: link with the TENANT_PHONE constant
 * Consumed by DispatcherSignOut (role="DRIVER"). PunchScreen adopts it
 * Tier-2 (§8); DriverLogin/WaitingScreen/DispatchNfcGate keep their
 * copy strings and fold Tier-2 as well.
 */
export function noOpenShiftHelp(phoneState: TenantPhoneState): ReactNode {
  if (phoneState.status === "ok" && phoneState.phone === null) {
    return (
      <span className="muted" style={{ fontSize: "13px" }}>
        Office phone not configured
      </span>
    );
  }
  const shown =
    phoneState.status === "ok" && phoneState.phone !== null
      ? phoneState.phone
      : TENANT_PHONE; // loading OR fallback OR unreachable
  return (
    <a href={phoneHref(shown)} className="contact-link" style={{ fontSize: "13px" }}>
      Not clocked in? Call the office ({shown})
    </a>
  );
}

/** Cache hit-shape: same union minus the transient loading variant. */
type CachedState =
  | { status: "ok"; phone: string | null }
  | { status: "fallback"; phone: string };

/** Module-level cache. `undefined` = not yet fetched. */
let cached: CachedState | undefined = undefined;
let inflight: Promise<CachedState> | null = null;
const subscribers = new Set<(state: CachedState) => void>();

function notify(state: CachedState): void {
  for (const fn of subscribers) {
    try {
      fn(state);
    } catch {
      // Subscriber errors must not break the cache update.
    }
  }
}

/** Compute the cache result for a single fetch attempt. */
async function probeOnce(): Promise<CachedState> {
  try {
    // SPEC-66 Phase 7 audit note (BUG #1 disposition): this is the SPA's
    // only fetch that bypasses api.ts's rawRequest. GET-only BY DESIGN —
    // the global csrfGuard exempts GET/HEAD/OPTIONS. If this ever becomes
    // a POST (or any state-changing method), switch to rawRequest so it
    // inherits X-Eagle-CSRF + credentials + 401-replay — a raw fetch POST
    // would 403 csrf_required against the deployed guard.
    const res = await fetch(`${API_URL}/api/v1/tenant/phone`, { method: "GET" });
    if (!res.ok) {
      // HTTP error: not a network failure but still a "don't trust the
      // server" signal. Treat as fallback so the constant is rendered.
      // NOT cached — the next mount re-tries, the office may have just
      // re-saved the phone.
      return { status: "fallback", phone: TENANT_PHONE };
    }
    // Strict body classification. INVARIANT: only a well-formed 2xx
    // carrying an EXPLICIT `phone` value may produce `ok` — anything
    // else on a 2xx is an unusable response and falls back to the
    // tappable constant. The previous `res.json().catch(() => ({}))`
    // turned a malformed 200 into `{}`, which fell into the
    // missing/empty branch and rendered a DISABLED "Office phone not
    // configured" span — the driver couldn't call anyone. Parse
    // failure must be a fallback, not an ok+null.
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      // Malformed JSON → fallback (tappable constant).
      return { status: "fallback", phone: TENANT_PHONE };
    }
    if (body !== null && typeof body === "object" && "phone" in body) {
      const p = (body as { phone: unknown }).phone;
      if (typeof p === "string" && p.length > 0) {
        return { status: "ok", phone: p };
      }
      if (p === null) {
        // The office explicitly cleared the phone. The driver sees
        // the disabled "not configured" state — a deliberate operator
        // action, not a broken response.
        return { status: "ok", phone: null };
      }
    }
    // Missing key, wrong type, or empty string — none of these are
    // explicit operator signals. Treat as malformed → fallback.
    return { status: "fallback", phone: TENANT_PHONE };
  } catch {
    // Network failure / offline: not cached, fall back to the constant.
    return { status: "fallback", phone: TENANT_PHONE };
  }
}

/**
 * Fire the public phone fetch if not already in flight. Safe to call
 * from multiple mounts — concurrent callers share one fetch. Returns the
 * resolved cache state, which is then published to subscribers.
 */
function refresh(): Promise<CachedState> {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  inflight = probeOnce().then((state) => {
    // Only cache the "ok" states — fallback is a transient signal, not
    // an answer. A future mount in a different network might succeed.
    if (state.status === "ok") {
      cached = state;
    }
    notify(state);
    return state;
  }).finally(() => {
    inflight = null;
  });
  return inflight;
}

/** Reset the cache (testing only). */
export function _resetPhoneCache(): void {
  cached = undefined;
  inflight = null;
  notify({ status: "fallback", phone: TENANT_PHONE });
}

/**
 * useTenantPhone — tri-state. Returns the current state of the public
 * phone fetch. NEVER blocks render — initial paint is always
 * `{status: "loading"}`, which consumers translate into the constant
 * fallback immediately. When the fetch settles, the consumer re-renders
 * with the actual status.
 */
export function useTenantPhone(): TenantPhoneState {
  // Initial paint: loading. The consumer reads status and falls back to
  // the constant for first render — no flash, no waiting.
  const [state, setState] = useState<TenantPhoneState>(
    cached ? cached : { status: "loading" },
  );

  useEffect(() => {
    subscribers.add(setState);
    if (cached) {
      setState(cached);
    } else {
      void refresh();
    }
    return () => {
      subscribers.delete(setState);
    };
  }, []);

  return state;
}

/**
 * Build a `tel:` href from a phone string (digits only, with leading
 * +). Total over `string` — null is rejected at the call site (the
 * consumer must handle the null case by NOT rendering a `tel:` link).
 */
export function phoneHref(phone: string): string {
  return `tel:${phone.replace(/[^+\d]/g, "")}`;
}
